import { Routes, Route } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import Header from './pages/Header';
import Sidebar from './pages/Sidebar';
import Footer from './pages/Footer';
import Dashboard from './pages/Dashboard';

import CourseList from './pages/CourseList';
import CourseAdd from './pages/CourseAdd';
import CourseView from './pages/CourseView';
import CourseEdit from './pages/CourseEdit';

import ChannelList from './pages/ChannelList';
import ChannelAdd from "./pages/ChannelAdd";
import ChannelView from "./pages/ChannelView";
import ChannelEdit from "./pages/ChannelEdit";

import EmployeeList from './pages/EmployeeList';
import EmployeeAdd from './pages/EmployeeAdd';
import EmployeeView from './pages/EmployeeView';
import EmployeeEdit from './pages/EmployeeEdit';

const AdminApp = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const warningTimer = useRef();
  const logoutTimer = useRef();

  const handleLogout = useCallback(async (showToast = true) => {
    // Clear any scheduled timers
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);

    try {
      axios.defaults.withCredentials = true;
      // Notify backend to clear HTTP-only cookie (logout endpoint)
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // ignore errors — proceed to clear client state regardless
      console.warn('Logout request failed:', err?.message || err);
    }

    // Clear client-side session and UI state
    sessionStorage.removeItem('email');
    setEmail(null);
    // Dismiss any existing toasts and show a short confirmation
    toast.dismiss();
    if (showToast) {
      toast.success('คุณได้ออกจากระบบแล้ว', { duration: 3000 });
    }

    navigate('/panel/login');
  }, [API_URL, navigate]);

  const scheduleSessionExpiry = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    // Warning at 59 minutes, logout at 60 minutes
    warningTimer.current = setTimeout(() => {
      toast.dismiss();
      toast((t) => (
        <span>
          ⚠️ เซสชันจะหมดอายุใน 1 นาที<br />
          <button
            onClick={() => {
              toast.dismiss(t.id);
              // Call refreshSession directly without dependency
              (async () => {
                try {
                  axios.defaults.withCredentials = true;
                  const res = await axios.post(`${API_URL}/auth/refresh-token`, {}, { withCredentials: true });
                  if (res.data && res.data.email) {
                    sessionStorage.setItem('email', res.data.email);
                    setEmail(res.data.email);
                    toast.dismiss();
                    toast.success('🔄 รีเฟรช Session สำเร็จแล้ว');
                    scheduleSessionExpiry();
                  }
                } catch (err) {
                  toast.dismiss();
                  toast.error('❌ การรีเฟรช Session ล้มเหลว');
                  handleLogout();
                }
              })();
            }}
            className="text-blue-500 font-bold mr-2"
          >
            🔄 ต่ออายุ
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              handleLogout();
            }}
            className="text-red-500 font-bold"
          >
            🔒 ออกจากระบบ
          </button>
        </span>
      ), { duration: 59_000 });
    }, 3540_000); // 59 minutes
    logoutTimer.current = setTimeout(() => {
      toast.dismiss();
      toast.error('⏰ เซสชันหมดอายุแล้ว');
      handleLogout();
    }, 3600_000); // 60 minutes
  }, [API_URL, handleLogout]);

  useEffect(() => {
    axios.defaults.withCredentials = true;
    const verify = async () => {
      try {
        const res = await axios.post(`${API_URL}/auth/refresh-token`, {}, { withCredentials: true });
        if (res.data && res.data.email) {
          sessionStorage.setItem('email', res.data.email);
          setEmail(res.data.email);
          scheduleSessionExpiry();
        } else {
          navigate('/panel/login');
        }
      } catch (err) {
        navigate('/panel/login');
      }
    };
    verify();
    return () => {
      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
    };
  }, [API_URL, navigate, scheduleSessionExpiry]);

  // Don't render if not authenticated
  if (!email) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen">
      <Header email={email} onLogout={handleLogout} />
      <Toaster position="top-center" reverseOrder={false} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-4">
          <Routes>
            <Route path="/" element={<Dashboard />} />            
            <Route path="/course" element={<CourseList />} />            
            <Route path="/course/add" element={<CourseAdd />} />
            <Route path="/course/view/:id" element={<CourseView />} />
            <Route path="/course/edit/:id" element={<CourseEdit />} />

            <Route path="/channel" element={<ChannelList />} />
            <Route path="/channel/add" element={<ChannelAdd />} />
            <Route path="/channel/view/:id" element={<ChannelView />} />
            <Route path="/channel/edit/:id" element={<ChannelEdit />} />

            <Route path="/employee" element={<EmployeeList />} />
            <Route path="/employee/add" element={<EmployeeAdd />} />
            <Route path="/employee/view/:id" element={<EmployeeView />} />
            <Route path="/employee/edit/:id" element={<EmployeeEdit />} />            
          </Routes>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default AdminApp;
